import { Sites } from '@ps/platform-message-framework/common/Sites';
import { Cookie as CookieNames, FeatureFlag, OddsFormat } from '@ps/web-core-enums';
import { useFeature } from '@ps/web-core-store';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Cookies from 'universal-cookie';

import type { Dispatch } from '@swf/core/store';
import { select } from '@swf/core/store';
import { setOddsFormatCookie } from '@swf/core/utils/setOddsFormatCookie';

import useIFramePostMessage from '../hooks/useIFramePostMessage';

const getOddsFormat = (
  oddsFormat: unknown,
  isAmericanOddsSupported: boolean,
  site: Sites
): OddsFormat => {
  const defaultOddsFormatBySite: Partial<Record<Sites, OddsFormat>> = {
    [Sites.CA_ON]: isAmericanOddsSupported ? OddsFormat.American : OddsFormat.Decimal
  };
  const defaultOddsFormat = defaultOddsFormatBySite[site] ?? OddsFormat.Decimal;

  if (oddsFormat === OddsFormat.Fractional) {
    return OddsFormat.Fractional;
  }

  if (isAmericanOddsSupported && oddsFormat === OddsFormat.American) {
    return OddsFormat.American;
  }

  if (oddsFormat === OddsFormat.Decimal) {
    return OddsFormat.Decimal;
  }

  return defaultOddsFormat;
};

const cookies = new Cookies();
const updateOddsFormatEventType = 'updateOddsFormat';

export interface OddsContextValue {
  oddsFormat: OddsFormat;
  setOddsFormat: (oddsFormat: OddsFormat) => void;
}

export const OddsFormatContext = React.createContext<OddsContextValue>({
  oddsFormat: OddsFormat.Decimal,
  setOddsFormat: () => undefined
});

const OddsFormatProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const userOddsFormat = useSelector(select.account.getOddsFormat);
  const isAmericanOddsSupported = useFeature(FeatureFlag.ISPAmericanOdds);
  const site = useSelector(select.cms.getSiteId);
  const cookiesOddsFormat = Number(cookies.get(CookieNames.ODDS_FORMAT));
  const initialOddsFormat = getOddsFormat(
    userOddsFormat || cookiesOddsFormat,
    isAmericanOddsSupported,
    site
  );

  const isClientAuthenticated = useSelector(select.session.isClientAuthenticated);
  const [oddsFormat, setOddsFormat] = useState<OddsFormat>(initialOddsFormat);

  const {
    account: { updateAccount: updateAccountOddsFormat }
  } = useDispatch<Dispatch>();

  const eventListener = (newOddsFormat?: OddsFormat) => {
    if (newOddsFormat) {
      setOddsFormat(newOddsFormat);
      setOddsFormatCookie(newOddsFormat);
    }
  };

  const { postMessage } = useIFramePostMessage(updateOddsFormatEventType, eventListener);
  const saveOddsFormat = useCallback(
    (newOddsFormat: OddsFormat) => {
      setOddsFormat(newOddsFormat);

      setOddsFormatCookie(newOddsFormat);

      postMessage(newOddsFormat);

      if (isClientAuthenticated) {
        updateAccountOddsFormat({
          oddsFormat: newOddsFormat
        });
      }
    },
    [postMessage, isClientAuthenticated, updateAccountOddsFormat]
  );

  useEffect(() => {
    userOddsFormat && setOddsFormat(getOddsFormat(userOddsFormat, isAmericanOddsSupported, site));
  }, [isClientAuthenticated, isAmericanOddsSupported, userOddsFormat, site]);

  return (
    <OddsFormatContext.Provider value={{ oddsFormat, setOddsFormat: saveOddsFormat }}>
      {children}
    </OddsFormatContext.Provider>
  );
};

export default OddsFormatProvider;
